'use client';

import { useState, useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Provider } from "react-redux";
import { Toaster } from "react-hot-toast";
import toast from "react-hot-toast";

import store from "../store/index.js";
import { login, setAuthChecked } from "../store/userSlice.js";
import apiClient, { resetAuthGuard } from "../api/apiClient.js";
import { PORTAL_PATH_PREFIXES } from "../lib/routeScopes.js";

import { AppRouterCacheProvider } from "@mui/material-nextjs/v15-appRouter";
import ErrorBoundary from "../components/ErrorBoundary";
import Loader from "../components/Loader";
import { AppContextProvider } from "../context/AppContext";
import { ThemeProvider } from "../context/ThemeContext";
import { DashboardContextProvider } from "../context/DashboardContext";
import { requestNotificationPermission } from "../firebase.js";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,   // 5 minutes
      gcTime: 1000 * 60 * 10,     // 10 minutes (was cacheTime in v4)
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

async function registerFCMToken() {
  try {
    const token = await requestNotificationPermission();
    if (token) {
      await apiClient.post("auth/fcm-token", { token });
    }
  } catch (e) {
    // Silent fail — push notifications are optional
  }
}

export default function ClientProviders({ children }) {
  const pathname = usePathname();
  const [loading, setLoading] = useState(true);
  const authCheckRef = useRef(false);

  // Auth check: only on portal and auth routes. Public pages skip the check entirely.
  useEffect(() => {
    const isPortalOrAuthRoute = PORTAL_PATH_PREFIXES.some(prefix => pathname.startsWith(prefix));

    if (!isPortalOrAuthRoute) {
      setLoading(false);
      return;
    }

    // Prevent double-call from React StrictMode in development
    if (authCheckRef.current) return;
    authCheckRef.current = true;

    const checkAuth = async () => {
      try {
        const response = await apiClient.get("auth/me");
        if (response.data.status) {
          const userData = response.data.data;
          // Fresh successful auth — clear any prior logout latch in the API client.
          resetAuthGuard();
          store.dispatch(
            login({
              username: userData.username,
              email: userData.email,
              role: userData.role,
              user_id: userData.user_id,
              permissions: userData.permissions || [],
              is_super_admin: Boolean(userData.is_super_admin),
            })
          );

          // Register FCM token for push notifications
          registerFCMToken();

          // Handle ?snooze=N param (from push notification when app was closed)
          const urlParams = new URLSearchParams(window.location.search);
          const snoozeParam = urlParams.get("snooze");
          if (snoozeParam) {
            try {
              const notifRes = await apiClient.get("notifications");
              const notifications = notifRes.data?.notifications || [];
              const reminder = notifications.find(
                (n) => n.type?.startsWith("attendance_clock_") && !n.read_at
              );
              if (reminder) {
                await apiClient.post(`notifications/${reminder.id}/snooze`, { duration: parseInt(snoozeParam) });
              }
              window.history.replaceState({}, "", window.location.pathname);
            } catch (e) { /* silent */ }
          }
        } else {
          store.dispatch(setAuthChecked());
        }
      } catch (error) {
        store.dispatch(setAuthChecked());
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [pathname]);

  // FCM and snooze listeners — fire once globally, independent of route
  useEffect(() => {
    // Listen for FCM foreground messages
    const handleFCMMessage = (e) => {
      const { title, body } = e.detail || {};
      if (title) {
        toast(body || title, { icon: "🔔", duration: 8000 });
      }
      queryClient.invalidateQueries(["notifications"]);
    };
    window.addEventListener("fcm-message", handleFCMMessage);

    // Handle snooze done (API already called by service worker)
    let snoozeHandled = false;
    const handleSnoozeDone = (notificationId, duration) => {
      if (snoozeHandled) return;
      snoozeHandled = true;
      setTimeout(() => { snoozeHandled = false; }, 1000);

      window.dispatchEvent(new CustomEvent("notification-snoozed", { detail: { notificationId, duration } }));
      toast(`Snoozed for ${duration} minutes`, { icon: "⏰", duration: 3000 });
      setTimeout(() => queryClient.invalidateQueries(["notifications"]), 2000);
    };

    const handleSnoozeRequest = (duration) => {
      if (snoozeHandled) return;
      snoozeHandled = true;
      setTimeout(() => { snoozeHandled = false; }, 1000);

      apiClient.get("notifications").then((res) => {
        const reminder = (res.data?.notifications || []).find(
          (n) => n.type?.startsWith("attendance_clock_") && !n.read_at
        );
        if (reminder) {
          window.dispatchEvent(new CustomEvent("notification-snoozed", { detail: { notificationId: reminder.id, duration } }));
          toast(`Snoozed for ${duration} minutes`, { icon: "⏰", duration: 3000 });
          apiClient.post(`notifications/${reminder.id}/snooze`, { duration }).then(() => {
            setTimeout(() => queryClient.invalidateQueries(["notifications"]), 2000);
          });
        }
      });
    };

    // Listen via service worker postMessage
    const handleSWMessage = (event) => {
      if (event.data?.type === "SNOOZE_DONE") {
        handleSnoozeDone(event.data.notificationId, event.data.duration || 15);
      } else if (event.data?.type === "SNOOZE_REMINDER") {
        handleSnoozeRequest(event.data.duration || 15);
      }
    };
    navigator.serviceWorker?.addEventListener("message", handleSWMessage);

    // Listen via BroadcastChannel
    let bc;
    try {
      bc = new BroadcastChannel("snooze-channel");
      bc.onmessage = (event) => {
        if (event.data?.type === "SNOOZE_DONE") {
          handleSnoozeDone(event.data.notificationId, event.data.duration || 15);
        } else if (event.data?.type === "SNOOZE_REMINDER") {
          handleSnoozeRequest(event.data.duration || 15);
        }
      };
    } catch (e) {}

    return () => {
      window.removeEventListener("fcm-message", handleFCMMessage);
      navigator.serviceWorker?.removeEventListener("message", handleSWMessage);
      try { bc?.close(); } catch (e) {}
    };
  }, []);

  return (
    <AppRouterCacheProvider>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Provider store={store}>
          <ThemeProvider>
            <AppContextProvider>
              <DashboardContextProvider>
                <Toaster
                  position="top-right"
                  toastOptions={{
                    duration: 4000,
                    style: {
                      background: '#333',
                      color: '#fff',
                      zIndex: 9999999,
                    },
                  }}
                  containerStyle={{ zIndex: 9999999 }}
                />
                {loading ? <Loader /> : children}
              </DashboardContextProvider>
            </AppContextProvider>
          </ThemeProvider>
        </Provider>
      </QueryClientProvider>
    </ErrorBoundary>
    </AppRouterCacheProvider>
  );
}

